import { createProxyMiddleware } from 'http-proxy-middleware'
import type { NextApiRequest, NextApiResponse } from 'next'
import { mockInterceptor } from '../mocks'

export const config = {
  api: {
    externalResolver: true,
    bodyParser: false,
  },
}

export enum ProxyTo {
  VBASE = 'vbase',
  MYVTEX = 'myvtex',
  PROMOTIONS_STORAGE = 'promotions-storage',
}

const proxyMap = (
  to: ProxyTo,
  account: string,
  workspace: string,
  env: string
) => {
  const tenant = workspace === 'master' ? account : `${workspace}--${account}`
  const paths = {
    [ProxyTo.VBASE]: `http://infra.io.vtex.com/vbase/v2/${account}/`,
    [ProxyTo.MYVTEX]: `https://${tenant}.myvtex.com/api/`,
    [ProxyTo.PROMOTIONS_STORAGE]: VtexApiRoutes.formatPrivateRoute({
      vendor: 'vtex',
      name: 'admin-promotions-storage-manager',
      major: '0',
      account,
      workspace,
      path: '/_v/promotions/',
    }),
  }
  return paths[to]
}

type ApiRequest = NextApiRequest & {
  headers: {
    account: string
    workspace: string
    'vtex-commerce-env'?: string
    'x-vtex-proxy-to'?: ProxyTo
  }
}

const proxy = createProxyMiddleware({
  changeOrigin: true,
  pathRewrite: { '^/api/proxy/': '' },
  router: (req: ApiRequest) => {
    const { headers } = req
    const {
      account,
      workspace,
      'vtex-commerce-env': vtexCommerceEnv = 'stable',
      'x-vtex-proxy-to': proxyTo = ProxyTo.MYVTEX,
    } = headers

    return proxyMap(proxyTo, account, workspace, vtexCommerceEnv)
  },
  on: {
    proxyReq: (proxyReq, req) => {
      const vtexToken = req.headers['vtexidclientautcookie']
      if (vtexToken) {
        const requestCookie = req.headers['cookie'] ?? ''
        proxyReq.setHeader('Cookie', `VtexIdclientAutCookie=${vtexToken};${requestCookie}`)
      }
    },
  },
  logger: console,
})

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse
) {
  const mockReturn = await mockInterceptor(req, res)
  if (mockReturn) {
    return mockReturn
  }

  return proxy(req as unknown as ApiRequest, res)
}

type PrivateRouteInfo = {
  protocol?: 'http' | 'https'
  vendor: string
  name: string
  major: string | number
  account: string
  workspace: string
  path?: string
}

type PublicRouteInfo = {
  workspace: string
  account: string
  endpoint: string
  path: string
}

/**
 * Extracted from @vtex/api to avoid dependency on @vtex/api
 * @see https://github.com/vtex/node-vtex-api/blob/master/src/service/worker/runtime/http/routes.ts
 * To define if a route is private or public, we need to check the definition below
 * @see https://github.com/vtex/admin-promotions-storage-manager/blob/a8096cbc418d40db0e08a73d1e761af06fa76f43/node/service.json#L15
 */
const VtexApiRoutes = {
  formatPrivateRoute: ({
    protocol = 'https',
    vendor,
    name,
    major,
    account,
    workspace,
    path,
  }: PrivateRouteInfo) =>
    `${protocol}://app.io.vtex.com/${vendor}.${name}/v${major}/${account}/${workspace}${
      path || ''
    }`,
  formatPublicRoute: ({
    workspace,
    account,
    endpoint = 'myvtex.com',
    path,
  }: PublicRouteInfo) => `https://${workspace}--${account}.${endpoint}${path}`,
}
